/**
 * Lightweight i18n (internationalization) module
 * Supports English and Italian based on browser language settings
 */

// Locale codes for date formatting corresponding to language codes
const localeMap = {
  en: 'en-CH',  // English - using Swiss English format (DD.MM.YYYY)
  it: 'it-CH'   // Italian - using Swiss Italian format
};

// Translation dictionaries
const translations = {
  en: {
    // Coffee tracker translations
    "daily-ranking": "Daily ranking",
    "todays-champion": "Today's champion",
    "no-data-today": "No data for today yet.",
    "champion-label": "Champion:",
    "coffees-today": "coffees today",
    "active": "active",
    "today": "Today",
    // Coffee stats translations
    "coffee-tracker": "Coffee tracker",
    "week": "Week",
    "month": "Month",
    "year": "Year",
    "total-drank": "Total drank",
    "total-offered": "Total offered",
    "generosity": "Generosity",
    "avg-per-active-day": "avg {avg} / active day",
    "of-drank": "",
    "share-rate": "% share rate",
    "high": "High",
    "mid": "Mid",
    "low": "Low",
    "drank": "Drank",
    "offered": "Offered",
    "marked": "Marked",
    "previous-period": "Previous period",
    "next-period": "Next period",
    "high": "High", 
    "mid": "Mid",
    "low": "Low",
    // Transaction ledger translations
    "ledger": "Ledger",
    "expenses": "Expenses",
    "payments": "Payments",
    "balance": "Balance",
    "search-description": "Search description…",
    "all-types": "All types",
    "expenses-only": "Expenses only",
    "payments-only": "Payments only",
    "from-date": "From date",
    "to-date": "To date",
    "date": "Date",
    "description": "Description",
    "amount": "Amount",
    "sort-by-date": "Sort by date",
    "sort-by-amount": "Sort by amount",
    "no-transactions": "No transactions match your filters.",
    "transaction-sg": "transaction",
    "transaction-pl": "transactions",
    "net": "Net:",
    // User profile translations
    "save-preferences": "Save preferences",
    "cancel-preferences": "Cancel",
    "crop-avatar": "Crop avatar",
    "crop-photo": "Crop photo",
    "crop-hint": "Drag to reposition · pinch or slider to zoom",
    "zoom": "Zoom",
    "cancel": "Cancel",
    "apply": "Apply",
    "close": "Close",
    "change-avatar": "Change avatar",
    "account-info": "Account info",
    "first-name": "First name",
    "last-name": "Last name",
    "email": "Email",
    "office": "Office",
    "coffee-icons": "Coffee icons",
    "drank-icon": "Drank icon",
    "offered-icon": "Offered icon",
    "marked-icon": "Marked icon",
    "preferences": "Preferences",
    "appearance": "Appearance",
    "app-colour-scheme": "Colour scheme",
    "push-notifications": "Push notifications",
    "get-alerts": "Get alerts for new activity",
    "check-updates": "Check for updates",
    "notify-updates": "Notify when a new version is available",
    "preferences-saved": "Preferences saved successfully.",
    "light": "Light",
    "auto": "Auto",
    "dark": "Dark",
    "napoli": "Napoli",
    "kazaar": "Kazaar",
    "ristretto": "Ristretto",
    "arpeggio-deca": "Arpeggio Deca",
    "nicaragua": "Nicaragua",
    "rio": "Rio",
    "roma": "Roma",
    "paris": "Paris",
    "hawaii": "Hawaii",
    "buenosaires": "Buenos Aires",
    "unable-notifications": "Unable to enable notifications",
    "about": "About",
    "app-version": "Ver",
    "release-notes": "App version",
    "cleanup": "Cache",
    "cleanup-desc": "Empty cache",
    "terms-of-service": "Terms",
    "terms-of-service-desc": "Terms of Service",
    "privacy-policy": "Privacy",
    "privacy-policy-desc": "Privacy Policy",
    // Push notification composer translations
    "pn-send-to": "Send to",
    "pn-loading": "Loading…",
    "pn-everyone": "Everyone",
    "pn-users-with-push": "{count} users with push on",
    "pn-message": "Message",
    "pn-message-placeholder": "Write your notification message…",
    "pn-to": "To:",
    "pn-send": "Send",
    "pn-sent-success": "Notification sent successfully.",
    "pn-sent-to": "Sent to {label}.",
    // Notification Inbox
    "inbox": "Inbox",
    "inbox-all": "All",
    "inbox-unread": "Unread",
    "inbox-read": "Read",
    "inbox-mark-all-read": "Mark all as read",
    "inbox-mark-read": "Mark as read",
    "inbox-delete": "Delete",
    "inbox-delete-notification": "Delete notification",
    "inbox-no-notifications": "No notifications",
    "inbox-no-unread": "No unread notifications",
    "inbox-no-read": "No read notifications yet",
    "inbox-just-now": "just now",
    "inbox-m-ago": "{m}m ago",
    "inbox-h-ago": "{h}h ago",
    "inbox-yesterday": "yesterday",
    "inbox-d-ago": "{d}d ago",
    "inbox-notification-sg": "notification",
    "inbox-notification-pl": "notifications",
    // Update prompt translations
    "update-required-title": "Required update",
    "update-required-body": "{ver} is now required to keep using the app.\nThis update includes critical fixes and security improvements.\nPlease restart the app to continue.",
    "update-required-btn": "Update & restart",
    "update-available-title": "Update available",
    "update-available-body": "{ver} is ready to install with new features and improvements.\nYou can update now or continue and we'll remind you next time.",
    "update-btn-later": "Later",
    "update-btn-now": "Update now",
    "update-new-version": "a new version",
    // Release notes translations
    "rn-header-whatsnew": "What's new",
    "rn-version": "Version",
    "rn-loading": "Loading release notes…",
    "rn-btn-next": "Next",
    "rn-btn-get-started": "Get started",
    "rn-close": "Close",
    "rn-go-to-page": "Go to page {n}",
    "rn-whatsnew-in": "What's new in version {ver}",
    // Cleanup form translations
    "cu-title": "Clean up data",
    "cu-desc": "Select what you'd like to clear. This action cannot be undone.",
    "cu-select-all": "Select all",
    "cu-cache": "PWA cache",
    "cu-cache-sub": "Clears service worker & cached assets",
    "cu-local-storage": "Local configurations",
    "cu-local-storage-sub": "Removes all locally stored key-value data",
    "cu-session-storage": "Session data",
    "cu-session-storage-sub": "Removes all session data",
    "cu-user-stats": "User stats",
    "cu-user-stats-sub": "Resets per-user activity & usage counters",
    "cu-admin-stats": "Admin stats",
    "cu-admin-stats-sub": "Resets aggregated admin-level analytics",
    "cu-warning": "Cleared data cannot be recovered.",
    "cu-run": "Run cleanup",
    "cu-cleared": "Cleared: {items}",
    "cu-completed": "Cleanup completed.",
    // Bottom-Nav
    "home": "Home",
    "stats": "Stats",
    "messages": "Messagges",
    "admin": "Admin",
    "logout": "Logout",
    // User-List
    "today": "Today",
    "yesterday": "Yesterday",
    "day-ago": "day ago",
    "unknown": "Unknown",
  },
  it: {
    // Coffee tracker translations
    "daily-ranking": "Classifica giornaliera",
    "todays-champion": "Il campione di oggi",
    "no-data-today": "Nessun dato per oggi.",
    "champion-label": "Campione:",
    "coffees-today": "caffè oggi",
    "active": "attivi",
    "today": "Oggi",
    // Coffee stats translations
    "coffee-tracker": "Tracciamento caffè",
    "week": "Settimana",
    "month": "Mese",
    "year": "Anno",
    "total-drank": "Totali bevuti",
    "total-offered": "Totali offerti",
    "generosity": "Generosità",
    "avg-per-active-day": "{avg} gg.",
    "of-drank": "",
    "share-rate": "% tasso di condivisione",
    "high": "Alta",
    "mid": "Media",
    "low": "Bassa",
    "drank": "Bevuti",
    "offered": "Offerti",
    "marked": "Marcati",
    "previous-period": "Periodo precedente",
    "next-period": "Periodo successivo",
    "high": "Alta", 
    "mid": "Media",
    "low": "Bassa",
    // Transaction ledger translations
    "ledger": "Registro",
    "expenses": "Spese",
    "payments": "Pagamenti",
    "balance": "Saldo",
    "search-description": "Cerca descrizione…",
    "all-types": "Tutti i tipi",
    "expenses-only": "Solo spese",
    "payments-only": "Solo pagamenti",
    "from-date": "Dal giorno",
    "to-date": "Al giorno",
    "date": "Data",
    "description": "Descrizione",
    "amount": "Importo",
    "sort-by-date": "Ordina per data",
    "sort-by-amount": "Ordina per importo",
    "no-transactions": "Nessuna transazione corrisponde ai filtri.",
    "transaction-sg": "transazione",
    "transaction-pl": "transazioni",
    "net": "Netto:",
    // User profile translations
    "save-preferences": "Salva preferenze",
    "cancel-preferences": "Esci",
    "crop-avatar": "Ritaglia avatar",
    "crop-photo": "Ritaglia foto",
    "crop-hint": "Trascina per riposizionare · pizzica o scorri per zoomare",
    "zoom": "Zoom",
    "cancel": "Annulla",
    "apply": "Applica",
    "close": "Chiudi",
    "change-avatar": "Cambia avatar",
    "account-info": "Informazioni account",
    "first-name": "Nome",
    "last-name": "Cognome",
    "email": "Email",
    "office": "Ufficio",
    "coffee-icons": "Icone caffè",
    "drank-icon": "Icona bevuti",
    "offered-icon": "Icona offerti",
    "marked-icon": "Icona marcati",
    "preferences": "Preferenze",
    "appearance": "Aspetto",
    "app-colour-scheme": "Schema colori",
    "push-notifications": "Notifiche push",
    "get-alerts": "Ricevi avvisi per nuova attività",
    "check-updates": "Controlla aggiornamenti",
    "notify-updates": "Avvisa quando è disponibile una nuova versione",
    "preferences-saved": "Preferenze salvate con successo.",
    "light": "Chiaro",
    "auto": "Auto",
    "dark": "Scuro",
    "napoli": "Napoli",
    "kazaar": "Kazaar",
    "ristretto": "Ristretto",
    "arpeggio-deca": "Arpeggio Deca",
    "nicaragua": "Nicaragua",
    "rio": "Rio",
    "roma": "Roma",
    "paris": "Paris",
    "hawaii": "Hawaii",
    "buenosaires": "Buenos Aires",
    "unable-notifications": "Impossibile abilitare le notifiche",
    "about": "Informazioni App CC-Caffè",
    "app-version": "Ver",
    "release-notes": "Versione dell'App",
    "cleanup": "Cache",
    "cleanup-desc": "Svuota cache",
    "terms-of-service": "Termini del servizio",
    "terms-of-service-desc": "Termini di Servizio di CC-Caffè App",
    "privacy-policy": "Privacy",
    "privacy-policy-desc": "Normativa sulla privacy",
    // Push notification composer translations
    "pn-send-to": "Invia a",
    "pn-loading": "Caricamento…",
    "pn-everyone": "Tutti",
    "pn-users-with-push": "{count} utenti con push attivo",
    "pn-message": "Messaggio",
    "pn-message-placeholder": "Scrivi il tuo messaggio di notifica…",
    "pn-to": "A:",
    "pn-send": "Invia",
    "pn-sent-success": "Notifica inviata con successo.",
    "pn-sent-to": "Inviata a {label}.",
    // Notification Inbox
    "inbox": "Inbox",
    "inbox-all": "Tutti",
    "inbox-unread": "Non letti",
    "inbox-read": "Letti",
    "inbox-mark-all-read": "Segna tutti come letti",
    "inbox-mark-read": "Segna come letto",
    "inbox-delete": "Elimina",
    "inbox-delete-notification": "Elimina notifica",
    "inbox-no-notifications": "Nessuna notifica",
    "inbox-no-unread": "Nessuna notifica non letta",
    "inbox-no-read": "Nessuna notifica letta",
    "inbox-just-now": "proprio ora",
    "inbox-m-ago": "{m} min fa",
    "inbox-h-ago": "{h} ore fa",
    "inbox-yesterday": "ieri",
    "inbox-d-ago": "{d} gg fa",
    "inbox-notification-sg": "notifica",
    "inbox-notification-pl": "notifiche",
    // Update prompt translations
    "update-required-title": "Aggiornamento obbligatorio",
    "update-required-body": "La versione {ver} è stata rilasciata. È necessario procedere con l'aggiornamento per continuare a usare l'app.\nQuesto aggiornamento include correzioni critiche e miglioramenti di sicurezza.\nRiavvia l'app per continuare.",
    "update-required-btn": "Aggiorna e riavvia",
    "update-available-title": "Nuova versione disponibile",
    "update-available-body": "La versione {ver} è pronta per essere installata con nuove funzionalità e miglioramenti.\nPuoi aggiornare ora o continuare. Ti ricorderemo dell'aggiornamento la prossima volta che utilizzerai l'App.",
    "update-btn-later": "Più tardi",
    "update-btn-now": "Aggiorna ora",
    "update-new-version": "una nuova versione",
    // Release notes translations
    "rn-header-whatsnew": "Novità",
    "rn-version": "Versione",
    "rn-loading": "Caricamento note di rilascio…",
    "rn-btn-next": "Avanti",
    "rn-btn-get-started": "Inizia",
    "rn-close": "Chiudi",
    "rn-go-to-page": "Vai a pagina {n}",
    "rn-whatsnew-in": "Novità nella versione {ver}",
    // Cleanup form translations
    "cu-title": "Pulisci dati",
    "cu-desc": "Seleziona cosa vuoi cancellare. Questa azione non può essere annullata.",
    "cu-select-all": "Seleziona tutto",
    "cu-cache": "Cache PWA",
    "cu-cache-sub": "Cancella service worker e risorse in cache",
    "cu-local-storage": "Configurazioni locali",
    "cu-local-storage-sub": "Rimuove tutti i dati salvati localmente",
    "cu-session-storage": "Dati sessione",
    "cu-session-storage-sub": "Rimuove tutti i dati della sessione",
    "cu-user-stats": "Statistiche utenti",
    "cu-user-stats-sub": "Reimposta i contatori di attività e utilizzo per utente",
    "cu-admin-stats": "Statistiche admin",
    "cu-admin-stats-sub": "Reimposta le statistiche aggregate a livello admin",
    "cu-warning": "I dati cancellati non possono essere recuperati.",
    "cu-run": "Avvia pulizia",
    "cu-cleared": "Cancellato: {items}",
    "cu-completed": "Pulizia completata.",
    // Bottom-Nav
    "home": "Home",
    "stats": "Stats",
    "messages": "Messaggi",
    "admin": "Admin",
    "logout": "Logout",
    // User-List
    "today": "Oggi",
    "yesterday": "Ieri",
    "day-ago": "giorni fa",
    "unknown": "&nbsp;",
  }
};

// Detected language (default to English)
let currentLang = 'en';
let currentLocale = 'en-CH';

/**
 * Detect user's preferred language from browser settings
 * Falls back to 'en' if Italian is not preferred
 */
function detectLanguage() {
  const browserLang = navigator.language || navigator.userLanguage;
  
  // Check if the browser language starts with 'it' (Italian)
  if (browserLang && browserLang.toLowerCase().startsWith('it')) {
    currentLang = 'it';
  } else {
    currentLang = 'en';
  }
  
  currentLocale = localeMap[currentLang];
  
  return currentLang;
}

/**
 * Set the current language manually
 * @param {string} lang - Language code ('en' or 'it')
 */
function setLanguage(lang) {
  if (translations[lang]) {
    currentLang = lang;
    currentLocale = localeMap[lang];
  }
}

/**
 * Get the current language
 * @returns {string} Language code
 */
function getLanguage() {
  return currentLang;
}

/**
 * Get the current locale for date/number formatting
 * @returns {string} Locale code (e.g., 'en-CH', 'it-CH')
 */
function getLocale() {
  return currentLocale;
}

/**
 * Get a translated string by key
 * @param {string} key - Translation key
 * @param {Object} [params] - Optional placeholder values (e.g., { ver: "1.2.3" })
 * @returns {string} Translated string or key if not found
 */
function t(key, params) {
  const langTranslations = translations[currentLang] || translations.en;
  let str = langTranslations[key] || translations.en[key] || key;
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      str = str.replace(`{${k}}`, v);
    }
  }
  return str;
}

// Auto-detect language on module load
detectLanguage();

export { t, setLanguage, getLanguage, getLocale, detectLanguage };
